import React from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";

const BIZ = {
  name: "Min3rva.org",
  email: "support@loompdf.com",
  address: "Bengaluru, Karnataka, India",
  product: "Loom",
  updated: "24 June 2026",
};

const DOCS = {
  terms: {
    title: "Terms & Conditions",
    sections: [
      ["Agreement", `By accessing or using ${BIZ.product} ("the Service"), operated by ${BIZ.name} ("we", "us"), you agree to these Terms & Conditions. If you do not agree, please do not use the Service.`],
      ["The Service", `${BIZ.product} lets users upload PDF documents and generates AI-assisted study material (section breakdowns, summaries and quizzes). The Service is provided on an "as is" and "as available" basis.`],
      ["Accounts", "You are responsible for maintaining the confidentiality of your account and for all activity under it. You must provide accurate information and be at least 13 years old (or have guardian consent) to use the Service."],
      ["Acceptable use", "You agree not to upload unlawful content, infringe others' intellectual property, attempt to breach security, or misuse the Service. You retain ownership of documents you upload; you grant us a limited licence to process them solely to provide the Service to you."],
      ["Subscriptions & payments", `Paid plans (Pro Monthly, Pro Annual, Lifetime) are billed in Indian Rupees (INR) through our payment processor, Razorpay. Prices and inclusions are shown on our Pricing page and may change with notice. Access to paid features is granted for the plan period purchased.`],
      ["Refunds", "All purchases are final and non-refundable. Please see our Refund & Cancellation Policy for details."],
      ["Intellectual property", `The ${BIZ.product} name, design, and software are owned by ${BIZ.name}. AI-generated study output is provided for your personal study use.`],
      ["Limitation of liability", "AI-generated content may contain inaccuracies and should not be relied upon as professional advice. To the maximum extent permitted by law, we are not liable for any indirect or consequential damages arising from use of the Service."],
      ["Termination", "We may suspend or terminate access for breach of these Terms. You may stop using the Service at any time."],
      ["Governing law", "These Terms are governed by the laws of India, with exclusive jurisdiction in the courts of Bengaluru, Karnataka."],
      ["Contact", `Questions about these Terms? Email ${BIZ.email}.`],
    ],
  },
  privacy: {
    title: "Privacy Policy",
    sections: [
      ["Overview", `This Privacy Policy explains how ${BIZ.name} collects, uses and protects your information when you use ${BIZ.product}.`],
      ["Information we collect", "Account details (name, email, profile picture from Google sign-in where used); documents you upload and the study material generated from them; usage data; and payment metadata processed by our payment processors, Razorpay and PayPal (we do not store your full card or bank details)."],
      ["How we use it", "To provide and improve the Service, generate your study material, manage your subscription, communicate with you about your account, and ensure security."],
      ["AI processing", "Uploaded PDFs are processed by AI models to produce sections, summaries and quizzes. Documents are used only to deliver the Service to you and are not sold."],
      ["Payments", "Payments are handled by Razorpay (for Indian Rupee payments) and PayPal (for international payments). When you pay, we share the information needed to process the transaction — such as your name, email, billing/country details, the plan purchased and the amount — with the relevant processor. Your card, UPI, bank or PayPal-account credentials are entered on Razorpay's or PayPal's secure systems and are never stored on our servers. Each processor's handling of your data is governed by its own privacy policy (see Razorpay's and PayPal's privacy policies)."],
      ["Cookies & sessions", "We use a session token (cookie and/or local storage) to keep you signed in. These are essential for the Service to function."],
      ["Data sharing", "We do not sell your personal data. We share data only with service providers (e.g. hosting, AI, Razorpay and PayPal) as needed to operate the Service, or where required by law."],
      ["Data retention & your rights", `We keep your data while your account is active. You may request access to or deletion of your data by emailing ${BIZ.email}.`],
      ["Security", "We use industry-standard measures to protect your data, though no method of transmission is 100% secure."],
      ["Contact", `For privacy questions, contact ${BIZ.email}.`],
    ],
  },
  refund: {
    title: "Refund & Cancellation Policy",
    sections: [
      ["Digital subscription", `${BIZ.product} is a digital subscription service providing immediate access to AI-generated study features. Plans are offered as Pro Monthly, Pro Annual and Lifetime, billed in INR via Razorpay.`],
      ["No refunds", "All purchases are final. Because access to digital features is granted immediately upon payment, we do not offer refunds for any plan, in whole or in part, including for partially used periods."],
      ["Cancellation", "You can stop using the Service at any time. Where a plan is for a fixed period, you will retain access until the end of the period you paid for; it will simply not renew. Lifetime plans are one-time purchases."],
      ["Failed or duplicate payments", `If you were charged but did not receive access, or were charged more than once for the same plan in error, email us at ${BIZ.email} with your payment reference and we will investigate and correct any genuine billing error.`],
      ["Contact", `For any billing query, contact ${BIZ.email}.`],
    ],
  },
  contact: {
    title: "Contact Us",
    sections: [
      ["Get in touch", `We'd love to hear from you. Reach ${BIZ.product}, operated by ${BIZ.name}, using the details below.`],
    ],
  },
};

export default function Legal({ kind }) {
  const doc = DOCS[kind] || DOCS.terms;
  return (
    <div className="relative min-h-screen grain" data-testid={`legal-${kind}`}>
      <Nav />
      <main className="relative z-10 max-w-3xl mx-auto px-6 pt-32 pb-24">
        <span className="tag-pill">legal</span>
        <h1 className="font-display text-4xl sm:text-5xl mt-4" data-testid="legal-title">{doc.title}</h1>
        <p className="text-stone-400 text-sm mt-2">Last updated: {BIZ.updated}</p>

        {kind === "contact" ? (
          <div className="mt-8 sticky-card p-8 space-y-5" data-testid="contact-details">
            <p className="text-stone-700">{doc.sections[0][1]}</p>
            <div className="space-y-3 text-stone-800">
              <div className="flex items-center gap-3"><span className="font-display text-lg w-28">Business</span><span data-testid="contact-name">{BIZ.name}</span></div>
              <div className="flex items-center gap-3"><Mail className="w-4 h-4 text-indigo-500"/><a href={`mailto:${BIZ.email}`} className="underline" data-testid="contact-email">{BIZ.email}</a></div>
              <div className="flex items-center gap-3"><MapPin className="w-4 h-4 text-indigo-500"/><span data-testid="contact-address">{BIZ.address}</span></div>
            </div>
            <p className="text-stone-500 text-sm">We typically respond within 1–2 business days.</p>
          </div>
        ) : (
          <div className="mt-8 space-y-7">
            {doc.sections.map(([h, p], i) => (
              <section key={i}>
                <h2 className="font-display text-xl mb-1.5">{h}</h2>
                <p className="text-stone-700 leading-relaxed">{p}</p>
              </section>
            ))}
          </div>
        )}

        <div className="mt-12 flex flex-wrap gap-4 text-sm text-stone-500">
          <Link to="/terms" className="hover:text-stone-900 underline">Terms</Link>
          <Link to="/privacy" className="hover:text-stone-900 underline">Privacy</Link>
          <Link to="/refund" className="hover:text-stone-900 underline">Refund & Cancellation</Link>
          <Link to="/contact" className="hover:text-stone-900 underline">Contact</Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
